import { useEffect, useRef, useState } from 'react';
import { useSimStore, applyInstructorPrefs, startInstructorPrefsPersistence } from '../../store/useSimStore';
import { buildPayload, applyPayload } from '../../sync/syncPayload';
import { useBroadcastSync } from '../../sync/useBroadcastSync';
import { useWebSocketSync } from '../../sync/useWebSocketSync';
import { updateTransition, TRANSITION_TICK_MS } from '../../engine/scenarioEngine';
import ScenarioSelector from './ScenarioSelector';
import VitalSliders from './VitalSliders';
import AlarmControls from './AlarmControls';
import TransitionControls from './TransitionControls';
import InterventionPanel from './InterventionPanel';
import MiniMonitor from './MiniMonitor';
import VentilatorControls from './VentilatorControls';
import CaseRecorder from './CaseRecorder';
import MonitorQrCode from './MonitorQrCode';
import HelpTip from './HelpTip';
import { EEG_BRANDS, EEG_BRAND_KEYS } from '../../engine/eegEngine';
import styles from './InstructorPanel.module.css';

// Channel toggle config: key matches visibleChannels, color matches waveform theme
const CHANNEL_DEFS = [
  { key: 'etco2', label: 'Capnography', sublabel: 'EtCO₂', color: '#FFD600' },
  { key: 'abp',   label: 'Arterial Line', sublabel: 'ABP', color: '#FF1744' },
  { key: 'cvp',   label: 'CVP Line', sublabel: 'CVP', color: '#26C6DA' },
  { key: 'temp',  label: 'Temperature', sublabel: 'Temp', color: '#FF9100' },
  { key: 'tof',   label: 'Neuromuscular', sublabel: 'TOF', color: '#B0BEC5' },
  { key: 'bis',   label: 'Depth (EEG)', sublabel: 'BIS', color: '#CF9FFF' },
];

const SNAPSHOT_KEY = 'sim.instructorSnapshot';
const SNAPSHOT_MAX_AGE_MS = 2 * 60 * 60 * 1000;   // older than 2 h = new session

export default function InstructorPanel() {
  const store = useSimStore();
  const transitionStartRef = useRef(null);
  const lastTickRef = useRef(null);
  const [showQr, setShowQr] = useState(false);

  // ── Resume after a refresh ─────────────────────────────────────────────────
  // The instructor tab owns the simulation; reloading it used to reset the
  // patient to baseline (and push that reset to every monitor). Restore the
  // last snapshot (same shape as the sync payload) if it is recent, plus the
  // instructor's persistent preferences. This effect is declared BEFORE the
  // sync hooks so the first broadcast already carries the restored state.
  useEffect(() => {
    applyInstructorPrefs();
    try {
      const saved = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || 'null');
      if (saved && Date.now() - saved.savedAt < SNAPSHOT_MAX_AGE_MS) applyPayload(saved.payload);
    } catch { /* ignore */ }
    const stopPrefs = startInstructorPrefsPersistence();
    // Throttled (not debounced): a 45 s vitals transition updates the store
    // every 50 ms, which would starve a trailing debounce forever.
    let timer = null;
    const unsub = useSimStore.subscribe(() => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        try { localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ savedAt: Date.now(), payload: buildPayload(useSimStore.getState()) })); } catch { /* ignore */ }
      }, 1000);
    });
    return () => { stopPrefs(); unsub(); clearTimeout(timer); };
  }, []);

  useBroadcastSync(true);
  // Two-device (LAN) mode: publish over the WebSocket relay too. No-op unless
  // WS mode is active on this page (see useWebSocketSync's resolveWsUrl).
  useWebSocketSync(true);

  // NIBP is owned by the monitor now (manual "NIBP Start" + "NIBP Auto Start"
  // q2.5-min cycling), so the instructor no longer drives an auto-cycle.

  // Watch isTransitioning: when applyVitals sets it true, record the start time
  // and begin the interpolation loop.
  useEffect(() => {
    if (!store.isTransitioning) {
      transitionStartRef.current = null;
      lastTickRef.current = null;
      return;
    }

    const now = Date.now();
    transitionStartRef.current = now;
    lastTickRef.current = now;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - transitionStartRef.current;
      // Real wall-clock time since the previous tick — NOT the nominal 50 ms.
      // Background tabs throttle setInterval to ~1 s; stepping by the real
      // interval keeps the ramp even instead of crawling then snapping.
      const sinceLastTick = now - lastTickRef.current;
      lastTickRef.current = now;
      // updateTransition reads store directly via getState to avoid stale closure
      updateTransition(useSimStore.getState(), elapsed, sinceLastTick);
    }, TRANSITION_TICK_MS);

    return () => clearInterval(interval);
  }, [store.isTransitioning]); // only dep is the boolean flag, not the whole store

  // Case recording: while a case is active, sample the vitals every 5 s (plus one
  // immediately at start). Timestamps come from Date.now() inside the action, so
  // throttling a backgrounded tab only makes samples sparser, never mistimed.
  const caseActive = store.caseRecording.active;
  useEffect(() => {
    if (!caseActive) return;
    const sample = () => useSimStore.getState().recordCaseSample();
    sample();
    const id = setInterval(sample, 5000);
    return () => clearInterval(id);
  }, [caseActive]);

  // Gradual rhythm changes: on any rhythm change, setRhythm reset rhythmProgress
  // to 0; ramp it to 1 over the transition duration so the monitor cross-fades
  // from the old morphology to the new one (both directions). Real elapsed time
  // drives it, so a throttled tab only updates it more coarsely, never mistimed.
  const rhythm = store.rhythm;
  useEffect(() => {
    if (useSimStore.getState().rhythmProgress >= 1) return; // no transition pending
    const dur = useSimStore.getState().transitionDuration;
    if (!dur) { useSimStore.getState().setRhythmProgress(1); return; }
    const t0 = Date.now();
    let id;
    const step = () => {
      const p = Math.min((Date.now() - t0) / dur, 1);
      useSimStore.getState().setRhythmProgress(p);
      if (p < 1) id = setTimeout(step, 60);
    };
    step();
    return () => clearTimeout(id);
  }, [rhythm]);

  return (
    <div className={styles.instructorPanel}>
      <MiniMonitor />
      {showQr && <MonitorQrCode onClose={() => setShowQr(false)} />}

      <header className={styles.header}>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>Anesthesia Monitor Simulator</h1>
          <span className={styles.subtitle}>Instructor Control Panel</span>
        </div>
        <div className={styles.creditRow}>
          <p className={styles.credit}>Created by Dr. Taylor Zhou FRCPC. All rights reserved.</p>
          <div className={styles.headerLinks}>
            <button
              className={styles.qrLink}
              onClick={() => setShowQr(true)}
              title="Show a QR code to open the monitor on another device"
            >
              ⧉ Scan for Monitor
            </button>
            <HelpTip label="About opening the monitor">
              <p><strong>Scan for Monitor</strong> shows a QR code so a tablet or TV browser can open the learner monitor. For two separate devices, start LAN Mode on this computer and keep both on the same wifi.</p>
              <p><strong>Open Monitor Screen</strong> opens the monitor in a new window on this device — drag it to a projector or second display.</p>
            </HelpTip>
            <a
              className={styles.monitorLink}
              href="/monitor"
              target="_blank"
              rel="noopener noreferrer"
              title="Open the patient monitor display in a new window"
            >
              🖥 Open Monitor Screen ↗
            </a>
            <a
              className={styles.instructionsToggle}
              href="/user-guide/index.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              User Guide ↗
            </a>
          </div>
        </div>

        {/* ── Patient demographics — syncs to the monitor's patient bar ───── */}
        <div className={styles.demographics}>
          <div className={styles.demographicsField}>
            <label className={styles.demographicsLabel} htmlFor="pt-name">Patient Name</label>
            <input
              id="pt-name"
              type="text"
              className={styles.demographicsInput}
              placeholder="DEMO PATIENT"
              value={store.patientName}
              onChange={(e) => store.setPatientField('patientName', e.target.value)}
            />
          </div>
          <div className={styles.demographicsField}>
            <label className={styles.demographicsLabel} htmlFor="pt-id">Patient ID / MRN</label>
            <input
              id="pt-id"
              type="text"
              className={styles.demographicsInput}
              placeholder="e.g. MRN-001234"
              value={store.patientId}
              onChange={(e) => store.setPatientField('patientId', e.target.value)}
            />
          </div>
          <div className={styles.demographicsFieldNarrow}>
            <label className={styles.demographicsLabel} htmlFor="pt-age-demo">Age (years)<HelpTip label="About patient details">Name, MRN and age appear on the monitor's patient bar. Age also adjusts the age-corrected MAC shown on the ventilator screen.</HelpTip></label>
            <input
              id="pt-age-demo"
              type="text"
              inputMode="decimal"
              className={styles.demographicsInput}
              placeholder="e.g. 55"
              value={store.patientAge ?? ''}
              onChange={(e) => store.setPatientField('patientAge', e.target.value || null)}
            />
          </div>
        </div>

      </header>

      <main className={styles.mainContent}>
        <CaseRecorder />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Monitor Channels<HelpTip label="About Monitor Channels">Turn monitor channels on or off. A hidden channel disappears from the learner monitor and its alarms are suppressed. <strong>Split Screen</strong> adds the Aisys ventilator beside the monitor; <strong>Pulse Pressure Var.</strong> adds respiratory swing to the arterial line and pleth for fluid-responsiveness teaching.</HelpTip></h2>
          <div className={styles.channelToggles}>
            {CHANNEL_DEFS.map(({ key, label, sublabel, color }) => {
              const isVisible = store.visibleChannels[key];
              return (
                <button
                  key={key}
                  className={`${styles.channelToggle} ${isVisible ? styles.channelToggleOn : styles.channelToggleOff}`}
                  onClick={() => store.setChannelVisible(key, !isVisible)}
                  title={isVisible ? `Hide ${label}` : `Show ${label}`}
                >
                  <span
                    className={styles.channelDot}
                    style={{ background: isVisible ? color : '#444' }}
                  />
                  <span className={styles.channelToggleName} style={isVisible ? { color } : {}}>
                    {label}
                  </span>
                  <span className={styles.channelToggleSub}>{sublabel}</span>
                  <span className={`${styles.channelToggleSwitch} ${isVisible ? styles.channelSwitchOn : ''}`} />
                </button>
              );
            })}

            {/* Split-screen ventilator toggle (not a waveform channel) */}
            <button
              className={`${styles.channelToggle} ${store.splitScreen ? styles.channelToggleOn : styles.channelToggleOff}`}
              onClick={() => {
                const on = store.splitScreen;
                store.setSplitScreen(!on);
                store.recordCaseEvent('screen', on ? 'Ventilator screen off' : 'Ventilator screen on');
              }}
              title={store.splitScreen ? 'Hide ventilator screen' : 'Show ventilator screen (split)'}
            >
              <span
                className={styles.channelDot}
                style={{ background: store.splitScreen ? '#66BB6A' : '#444' }}
              />
              <span className={styles.channelToggleName} style={store.splitScreen ? { color: '#66BB6A' } : {}}>
                Split Screen
              </span>
              <span className={styles.channelToggleSub}>Ventilator</span>
              <span className={`${styles.channelToggleSwitch} ${store.splitScreen ? styles.channelSwitchOn : ''}`} />
            </button>

            {/* Depth-of-anaesthesia screen (BIS / Entropy / SedLine) */}
            <button
              className={`${styles.channelToggle} ${store.eegMonitor.enabled ? styles.channelToggleOn : styles.channelToggleOff}`}
              onClick={() => {
                const on = store.eegMonitor.enabled;
                store.setEegMonitor({ enabled: !on });
                store.recordCaseEvent('screen', on ? 'Depth-of-anaesthesia screen off' : `Depth-of-anaesthesia screen on (${EEG_BRANDS[store.eegMonitor.brand].label})`);
              }}
              title="Show a processed-EEG depth-of-anaesthesia monitor beside the vitals monitor"
            >
              <span className={styles.channelDot} style={{ background: store.eegMonitor.enabled ? '#CF9FFF' : '#444' }} />
              <span className={styles.channelToggleName} style={store.eegMonitor.enabled ? { color: '#CF9FFF' } : {}}>
                Depth of Anaesthesia
              </span>
              <span className={styles.channelToggleSub}>BIS · Entropy · SedLine</span>
              <span className={`${styles.channelToggleSwitch} ${store.eegMonitor.enabled ? styles.channelSwitchOn : ''}`} />
            </button>

            {/* Pulse Pressure Variation — respiratory swing on the arterial line */}
            <button
              className={`${styles.channelToggle} ${store.ppv.enabled ? styles.channelToggleOn : styles.channelToggleOff}`}
              onClick={() => store.setPpv({ enabled: !store.ppv.enabled })}
              title="Show respiratory Pulse Pressure Variation on the arterial line + SpO₂ pleth (fluid-responsiveness teaching)"
            >
              <span className={styles.channelDot} style={{ background: store.ppv.enabled ? '#FF1744' : '#444' }} />
              <span className={styles.channelToggleName} style={store.ppv.enabled ? { color: '#FF1744' } : {}}>
                Pulse Pressure Var.
              </span>
              <span className={styles.channelToggleSub}>PPV · art + pleth</span>
              <span className={`${styles.channelToggleSwitch} ${store.ppv.enabled ? styles.channelSwitchOn : ''}`} />
            </button>
          </div>

          {store.eegMonitor.enabled && (
            <div className={styles.ppvControl}>
              <span className={styles.ppvLabel}>
                Device
                <HelpTip label="About the depth-of-anaesthesia monitor">All three brands read the same hypnotic index, which the sedatives and volatile agents you give drive automatically (propofol / volatile → 40–60, awake → 95+). The slider overrides it until the next anaesthetic drug changes it. SedLine adds four EEG leads and a density spectral array; Entropy shows RE / SE / BSR.</HelpTip>
              </span>
              {EEG_BRAND_KEYS.map((k) => (
                <button
                  key={k}
                  className={`${styles.eegBrandChip} ${store.eegMonitor.brand === k ? styles.eegBrandChipActive : ''}`}
                  style={store.eegMonitor.brand === k ? { borderColor: EEG_BRANDS[k].accent, color: EEG_BRANDS[k].accent } : {}}
                  onClick={() => store.setEegMonitor({ brand: k })}
                >
                  {EEG_BRANDS[k].label}
                </button>
              ))}
              <span className={styles.ppvLabel} style={{ marginLeft: 12 }}>Index</span>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={store.bisValue}
                onChange={(e) => store.setBIS(Number(e.target.value))}
                className={styles.ppvSlider}
                aria-label="Depth-of-anaesthesia index override"
              />
              <span className={styles.ppvValue}>{store.bisValue}</span>
            </div>
          )}

          {store.ppv.enabled && (
            <div className={styles.ppvControl}>
              <span className={styles.ppvLabel}>PPV magnitude</span>
              <input
                type="range"
                min="5"
                max="40"
                step="1"
                value={store.ppv.percent}
                onChange={(e) => store.setPpv({ percent: Number(e.target.value) })}
                className={styles.ppvSlider}
                aria-label="Pulse pressure variation magnitude"
              />
              <span className={styles.ppvValue}>{store.ppv.percent}%</span>
            </div>
          )}
        </section>

        <div className={styles.tuneRow}>
          <div className={styles.tuneLeft}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Transition Speed<HelpTip label="About Transition Speed">How quickly vitals move to new values when you load a scenario, give a drug, or move a slider. <strong>Instant</strong> jumps immediately; slower settings ramp realistically. Drug onsets scale with this setting too.</HelpTip></h2>
              <TransitionControls store={store} />
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Alarm Settings<HelpTip label="About Alarm Settings">Mutes or unmutes all alarm sounds on the learner monitor. Per-parameter alarm limits are set in the yellow (Lo) and orange (Hi) fields under each vital in Vital Parameters.</HelpTip></h2>
              <AlarmControls store={store} />
            </section>
          </div>

          <section className={`${styles.section} ${styles.tuneVitals}`}>
            <h2 className={styles.sectionTitle}>Vital Parameters<HelpTip label="About Vital Parameters">Set any single vital with − / + or by typing a target — it overrides the active scenario and ramps at the Transition Speed. Current shows the live value. The yellow (Lo) and orange (Hi) fields under each vital are its alarm limits.</HelpTip></h2>
            <VitalSliders store={store} />
          </section>
        </div>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Patient Scenario<HelpTip label="About Patient Scenario">Load a scenario to move the patient to its vitals, rhythm and capnogram. Hover a card for a preview, including the correct interventions learners should give. Use <strong>Custom</strong> to import your own scenarios, <strong>Replay</strong> to debrief a recorded case, and <strong>↺ Normal Baseline</strong> to reset everything.</HelpTip></h2>
          <ScenarioSelector store={store} />
        </section>

        {/* Ventilator settings — shown when split-screen is enabled */}
        {store.splitScreen && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Ventilator (Aisys CS2)<HelpTip label="About Ventilator (Aisys CS2)">Ventilator settings shown on the split-screen Aisys display: mode, tidal volume, rate, PEEP, FiO₂ and volatile agent. The screen stays in STANDBY until the patient is intubated (Airway intervention or an intubated scenario).</HelpTip></h2>
            <VentilatorControls />
          </section>
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Interventions<HelpTip label="About Interventions">Tap a card to give that drug, fluid or procedure — its effect ramps in and the card shows a countdown. Tap the chevron to see dose and expected effect. Effects depend on the active scenario (e.g. some drugs worsen certain crises). By default only interventions relevant to the scenario are shown; use <strong>Show all</strong> to see every option.</HelpTip></h2>
          <InterventionPanel />
        </section>
      </main>

      {/* Sticky quick-action bar: the things you reach for under pressure,
          always one tap away regardless of scroll position. */}
      <footer className={styles.footer}>
        <span className={store.isConnected ? styles.connected : styles.disconnected}>
          {store.isConnected ? '● Connected' : '○ Disconnected'}
        </span>
        <div className={styles.quickActions}>
          <button
            className={styles.quickBtn}
            onClick={() => store.undo()}
            disabled={store.undoStack.length === 0}
            title="Undo the last scenario / modifier change"
          >
            ↶ Undo
          </button>
          <button
            className={`${styles.quickBtn} ${styles.quickBtnBaseline}`}
            onClick={() => store.requestScenario('normal')}
            title="Reset the patient to Normal Baseline"
          >
            ↺ Baseline
          </button>
          <button
            className={styles.quickBtn}
            onClick={() => store.setAudioMuted(!store.audioMuted)}
            title={store.audioMuted ? 'Unmute monitor alarms' : 'Mute monitor alarms'}
          >
            {store.audioMuted ? '🔇 Muted' : '🔊 Audio'}
          </button>
        </div>
      </footer>
    </div>
  );
}
